import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { getIncludedOpenShiftsData } from "@src/appV2/redesign/Shift/Open/getIncludedOpenShiftsData";
import type { OpenShift, OpenShiftWorkplace } from "@src/appV2/redesign/Shift/Open/types";
import { useGetOpenShifts } from "@src/appV2/redesign/Shift/Open/useGetOpenShifts";
import { useGetShiftDiscoveryDefaultDateRange } from "@src/appV2/redesign/utils/useGetShiftDiscoveryDefaultDateRange";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { minutesToMilliseconds } from "date-fns";
import { useEffect, useMemo, useState } from "react";

const DEFAULT_NEAREST_OPEN_SHIFTS_CONFIG = {
  enabled: false,
  distanceTiersInMiles: [300, 500, 750, 1000],
  minimumShiftCount: 1,
};

const NEAREST_OPEN_SHIFTS_STALE_TIME = minutesToMilliseconds(5);

interface UseNearestOpenShiftsResult {
  shifts: OpenShift[];
  workplacesMap: Map<string, OpenShiftWorkplace>;
  effectiveDistanceLimitInMiles: number;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

interface UseNearestOpenShiftsParams {
  enabled: boolean;
}

/**
 * Hook that progressively queries the existing open-shifts endpoint with
 * increasing distance tiers (300 → 500 → 750 → 1000 miles) until at least
 * `minimumShiftCount` shifts are found. Results are sorted by distance ASC
 * client-side.
 *
 * This is used as a fallback when the worker's normal shift list is empty,
 * to show that shifts exist somewhere even if not nearby.
 */
export function useNearestOpenShifts(
  params: UseNearestOpenShiftsParams
): UseNearestOpenShiftsResult {
  const { enabled } = params;

  const flagConfig = useCbhFlag(CbhFeatureFlag.NEAREST_OPEN_SHIFTS, {
    defaultValue: DEFAULT_NEAREST_OPEN_SHIFTS_CONFIG,
  });
  const worker = useDefinedWorker();
  const workerGeoLocation = useWorkerGeoLocation();
  const defaultDateRange = useGetShiftDiscoveryDefaultDateRange();

  const distanceTiers = flagConfig.distanceTiersInMiles;
  const { minimumShiftCount } = flagConfig;
  const isActive = enabled && flagConfig.enabled && isDefined(workerGeoLocation);

  const [currentTierIndex, setCurrentTierIndex] = useState(0);
  const currentDistance = distanceTiers[currentTierIndex] ?? 0;
  const isLastTier = currentTierIndex >= distanceTiers.length - 1;

  const query = useGetOpenShifts(
    {
      filter: {
        area: {
          latitude: workerGeoLocation?.latitude ?? 0,
          longitude: workerGeoLocation?.longitude ?? 0,
          radiusInMiles: currentDistance,
        },
        start: [
          {
            gte: defaultDateRange.startDate.toISOString(),
            lte: defaultDateRange.endDate.toISOString(),
          },
        ],
      },
      include: ["workplace"],
    },
    {
      enabled: isActive,
      staleTime: NEAREST_OPEN_SHIFTS_STALE_TIME,
    }
  );

  const resultCount = query.data?.data.length ?? 0;
  const hasSufficientResults = query.isSuccess && resultCount >= minimumShiftCount;

  useEffect(() => {
    if (query.isSuccess && !hasSufficientResults && !isLastTier) {
      setCurrentTierIndex((previous) => previous + 1);
    }
  }, [query.isSuccess, hasSufficientResults, isLastTier, currentTierIndex]);

  const hasAnyResults = query.isSuccess && resultCount > 0;
  const shouldReturnResults = hasSufficientResults || (hasAnyResults && isLastTier);

  const result = useMemo(() => {
    if (shouldReturnResults && query.data) {
      const { workplacesMap } = getIncludedOpenShiftsData(query.data);
      const shifts = sortShiftsByDistance(query.data.data, workplacesMap, workerGeoLocation);
      return {
        shifts,
        workplacesMap,
        effectiveDistanceLimitInMiles: currentDistance,
      };
    }

    return {
      shifts: [] as OpenShift[],
      workplacesMap: new Map<string, OpenShiftWorkplace>(),
      effectiveDistanceLimitInMiles: currentDistance,
    };
  }, [shouldReturnResults, query.data, workerGeoLocation, currentDistance]);

  const isLoading = isActive && (query.isLoading || (!shouldReturnResults && !isLastTier));
  const isSuccess = !isActive || shouldReturnResults || (query.isSuccess && isLastTier);

  const isSearchDone = isActive && (shouldReturnResults || (query.isSuccess && isLastTier));

  useLogEffect(
    APP_V2_USER_EVENTS.NEAREST_OPEN_SHIFTS_SEARCH_COMPLETED,
    {
      workerId: worker.userId,
      workerLatitude: workerGeoLocation?.latitude,
      workerLongitude: workerGeoLocation?.longitude,
      distanceTiersInMiles: distanceTiers,
      effectiveDistanceLimitInMiles: currentDistance,
      shiftsReturned: resultCount,
      minimumShiftCount,
      allTiersExhausted: isLastTier && !hasSufficientResults,
    },
    { enabled: isSearchDone }
  );

  return {
    ...result,
    isLoading,
    isSuccess,
    isError: query.isError,
  };
}

/**
 * Sorts shifts by distance from the worker's location (ascending).
 * Uses a simple Haversine approximation via latitude/longitude difference.
 */
function sortShiftsByDistance(
  shifts: OpenShift[],
  workplacesMap: Map<string, OpenShiftWorkplace>,
  workerGeoLocation: { latitude: number; longitude: number } | undefined
): OpenShift[] {
  if (!isDefined(workerGeoLocation)) {
    return shifts;
  }

  return [...shifts].sort((a, b) => {
    const workplaceA = workplacesMap.get(a.relationships.workplace.data.id);
    const workplaceB = workplacesMap.get(b.relationships.workplace.data.id);

    const distanceA = getApproximateDistance(workerGeoLocation, workplaceA?.attributes.location);
    const distanceB = getApproximateDistance(workerGeoLocation, workplaceB?.attributes.location);

    return distanceA - distanceB;
  });
}

function getApproximateDistance(
  origin: { latitude: number; longitude: number },
  destination: { latitude: number; longitude: number } | undefined
): number {
  if (!isDefined(destination)) {
    return Number.MAX_SAFE_INTEGER;
  }

  const latDiff = origin.latitude - destination.latitude;
  const lngDiff = origin.longitude - destination.longitude;
  return latDiff * latDiff + lngDiff * lngDiff;
}
